import { useEffect, useRef, useState } from "react";
import { BrowserQRCodeReader } from "@zxing/browser";
import { X, Camera, AlertTriangle } from "lucide-react";

const CHECKIN_RE = /\/checkin\/([^/?#]+)\/([^/?#]+)/;

// Extrai {path, lessonId} de um QR (URL absoluta da mesma origem ou relativa).
// Retorna null se o QR não for um QR de presença válido do Doxa School.
function parseCheckin(text) {
  if (!text || typeof text !== "string") return null;
  const raw = text.trim();
  let pathToTest = raw;
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      if (u.origin !== window.location.origin) return null; // não navegar para domínio externo
      pathToTest = u.pathname;
    } catch {
      return null;
    }
  }
  const m = pathToTest.match(CHECKIN_RE);
  if (!m) return null;
  const lessonId = m[1];
  const token = m[2];
  if (!lessonId || !token) return null;
  return { path: `/checkin/${lessonId}/${token}`, lessonId };
}

export default function QrCodeScannerModal({ open, onClose, onDetected, expectedLessonId }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const handledRef = useRef(false);
  const [status, setStatus] = useState("loading"); // loading | scanning | denied | unsupported
  const [errorText, setErrorText] = useState("");

  const stopCamera = () => {
    try { controlsRef.current?.stop(); } catch { /* noop */ }
    controlsRef.current = null;
    const stream = videoRef.current?.srcObject;
    if (stream && stream.getTracks) stream.getTracks().forEach((t) => t.stop());
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  useEffect(() => {
    if (!open) return;
    handledRef.current = false;
    setErrorText("");
    setStatus("loading");

    const hasMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    if (!hasMedia) {
      setStatus("unsupported");
      return;
    }

    const reader = new BrowserQRCodeReader();
    let cancelled = false;

    reader
      .decodeFromConstraints(
        { video: { facingMode: { ideal: "environment" } } },
        videoRef.current,
        (result, err, controls) => {
          if (cancelled || handledRef.current) return;
          if (controls && !controlsRef.current) {
            controlsRef.current = controls;
            setStatus("scanning");
          }
          if (result) {
            const text = result.getText();
            const parsed = parseCheckin(text);
            if (!parsed) {
              setErrorText("Este QR Code não é um QR de presença válido.");
              return;
            }
            if (expectedLessonId && parsed.lessonId !== expectedLessonId) {
              setErrorText("Este QR Code pertence a outra aula.");
              return;
            }
            // Trava de processamento: só uma leitura válida é tratada
            handledRef.current = true;
            setErrorText("");
            stopCamera();
            onDetected(parsed.path);
          }
        }
      )
      .catch((e) => {
        if (cancelled) return;
        const name = e?.name || "";
        if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") {
          setStatus("denied");
        } else {
          setStatus("unsupported");
        }
      });

    return () => { cancelled = true; stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, expectedLessonId]);

  const handleClose = () => { stopCamera(); onClose(); };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="qr-scanner-modal">
      <div className="bg-[#F8F3EA] w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#EBE5DB]">
          <h2 className="font-display text-lg font-bold inline-flex items-center gap-2">
            <Camera className="w-5 h-5 text-[#F75A1B]" /> Escanear QR Code da aula
          </h2>
          <button onClick={handleClose} className="p-1 rounded-lg hover:bg-[#EBE5DB]" data-testid="qr-scanner-close" aria-label="Fechar">
            <X className="w-5 h-5 text-[#4A4640]" />
          </button>
        </div>

        <div className="p-5">
          <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-black">
            <video ref={videoRef} className="w-full h-full object-cover" muted playsInline data-testid="qr-scanner-video" />
            {(status === "scanning") && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="w-2/3 h-2/3 border-2 border-[#F75A1B] rounded-2xl" />
              </div>
            )}
            {status === "loading" && (
              <div className="absolute inset-0 flex items-center justify-center text-white text-sm" data-testid="qr-scanner-loading">
                Abrindo câmera...
              </div>
            )}
          </div>

          {status === "scanning" && (
            <p className="text-sm text-[#6B655E] mt-3 text-center" data-testid="qr-scanner-instruction">
              Aponte a câmera para o QR Code exibido pelo professor.
            </p>
          )}

          {errorText && (
            <p className="text-sm text-[#C53030] mt-3 text-center inline-flex items-center gap-1 justify-center w-full" data-testid="qr-scanner-invalid">
              <AlertTriangle className="w-4 h-4" /> {errorText}
            </p>
          )}

          {status === "denied" && (
            <div className="mt-3 text-center" data-testid="qr-scanner-denied">
              <AlertTriangle className="w-8 h-8 mx-auto text-[#C05621]" />
              <p className="text-sm text-[#4A4640] mt-2">
                Não foi possível acessar a câmera. Autorize o uso da câmera ou use a câmera do celular para escanear o QR Code.
              </p>
            </div>
          )}

          {status === "unsupported" && (
            <div className="mt-3 text-center" data-testid="qr-scanner-unsupported">
              <AlertTriangle className="w-8 h-8 mx-auto text-[#C05621]" />
              <p className="text-sm text-[#4A4640] mt-2">
                Scanner indisponível neste navegador. Use a câmera nativa do celular para escanear o QR Code.
              </p>
            </div>
          )}

          <p className="text-xs text-[#6B655E] mt-4 text-center">
            Você também pode usar a câmera normal do celular para escanear o QR Code.
          </p>

          <button onClick={handleClose} className="w-full mt-4 py-2.5 rounded-xl font-semibold bg-[#F3EFE7] text-[#4A4640] hover:bg-[#EBE5DB] transition-colors" data-testid="qr-scanner-close-btn">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
